// Layout — T051
import { useState } from 'react';
import { Link, Outlet, useLocation } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { useAppSettings } from '../hooks/useAppSettings';

interface NavItem {
  path: string;
  label: string;
  adminOnly?: boolean;
}

const navItems: NavItem[] = [
  { path: '/', label: 'Dashboard' },
  { path: '/warroom', label: 'War Room' },
  { path: '/users', label: 'Users', adminOnly: true },
  { path: '/status-pages', label: 'Status Pages', adminOnly: true },
  { path: '/settings', label: 'Settings', adminOnly: true },
  { path: '/import-export', label: 'Import/Export', adminOnly: true },
];

export default function Layout() {
  const { user, isAdmin, logout } = useAuth();
  const { appTitle } = useAppSettings();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const visibleNav = navItems.filter((item) => !item.adminOnly || isAdmin);

  return (
    <div className="min-h-screen bg-bg-primary flex">
      {/* Mobile header */}
      <div className="md:hidden fixed top-0 left-0 right-0 z-50 bg-surface border-b border-border px-4 py-3 flex items-center justify-between">
        <button onClick={() => setSidebarOpen(!sidebarOpen)} className="text-text-primary min-h-[44px] min-w-[44px] flex items-center justify-center" aria-label="Toggle menu">
          <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M4 6h16M4 12h16M4 18h16" />
          </svg>
        </button>
        <span className="text-white font-bold text-lg">{appTitle}</span>
        <button onClick={logout} className="text-text-secondary hover:text-text-primary min-h-[44px] min-w-[44px] flex items-center justify-center" aria-label="Sign out">
          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 9V5.25A2.25 2.25 0 0013.5 3h-6a2.25 2.25 0 00-2.25 2.25v13.5A2.25 2.25 0 007.5 21h6a2.25 2.25 0 002.25-2.25V15m3-3l3-3m0 0l-3-3m3 3H9" />
          </svg>
        </button>
      </div>

      {/* Sidebar */}
      <aside className={`
        fixed md:sticky top-0 left-0 z-40 h-screen w-64 bg-surface border-r border-border flex flex-col transition-transform
        ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'} md:translate-x-0
        2xl:w-72
      `}>
        <div className="p-6 border-b border-border">
          <h1 className="text-white font-bold text-h2">{appTitle}</h1>
        </div>

        <nav className="flex-1 p-4 space-y-1">
          {visibleNav.map((item) => (
            <Link
              key={item.path}
              to={item.path}
              onClick={() => setSidebarOpen(false)}
              className={`px-4 py-3 rounded-lg text-sm font-medium transition-colors min-h-[44px] flex items-center ${
                location.pathname === item.path
                  ? 'bg-brand text-white'
                  : 'text-text-secondary hover:text-text-primary hover:bg-surface-hover'
              }`}
            >
              {item.label}
            </Link>
          ))}
        </nav>

        <div className="p-4 border-t border-border">
          <div className="text-text-secondary text-sm mb-2 truncate">{user?.email}</div>
          <div className="text-text-muted text-xs mb-3">{user?.role}</div>
          <button
            onClick={logout}
            className="w-full text-left px-4 py-2 rounded text-text-secondary hover:text-text-primary hover:bg-surface-hover text-sm min-h-[44px]"
          >
            Sign Out
          </button>
        </div>
      </aside>

      {/* Overlay for mobile */}
      {sidebarOpen && (
        <div className="md:hidden fixed inset-0 z-30 bg-black/50" onClick={() => setSidebarOpen(false)} />
      )}

      {/* Main content */}
      <main className="flex-1 min-h-screen mt-14 md:mt-0">
        <div className="p-4 md:p-6 lg:p-8 max-w-7xl mx-auto 2xl:max-w-full">
          <Outlet />
        </div>
      </main>
    </div>
  );
}
